import { ErrorBoundary } from "@sentry/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { TicketCategoryList } from "../components/TicketCategoryList";
import { TicketCategoryTree } from "../components/TicketCategoryTree";
import { canManageCategories } from "../permissions";

export function TicketCategoryOverview(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  return (
    <DocumentPaper
      title={t("page.repair-category.list.title")}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.tickets.title"),
              to: routes.tickets.list({
                slug,
              }),
            },
            {
              name: t("page.repair-category.list.title"),
            },
          ]}
        />
      }
      actions={
        hasPermission(canManageCategories) ? (
          <Button
            type="link"
            href={routes.ticketCategories.create({
              slug,
            })}
            data-testid="create-category-link"
          >
            {t("model.repair-category.action.create")}
          </Button>
        ) : null
      }
    >
      <ErrorBoundary fallback={(error) => <ErrorPage error={error} />}>
        <Suspense fallback={<FullSizeLoader withPadding />}>
          <Content />
        </Suspense>
      </ErrorBoundary>
    </DocumentPaper>
  );
}

function Content() {
  const ticketCategoryQueries = useTicketCategoryQueries();
  const { data } = useSuspenseQuery(ticketCategoryQueries.ticketCategoryTreeSettings());

  return (
    <div className="flex flex-col gap-6">
      <TicketCategoryList />
      {data.enabled && <TicketCategoryTree />}
    </div>
  );
}
